/**
 * Face Detection Model Loader
 *
 * Handles lazy loading of face-api.js models with caching and error handling.
 * Models are loaded only when ZK authentication is first used.
 */

import * as faceapi from 'face-api.js';

// Make faceapi available globally for other modules
(window as any).faceapi = faceapi;

console.log('[ZK Model Loader] face-api.js imported:', !!faceapi);
console.log('[ZK Model Loader] Available nets:', Object.keys(faceapi.nets || {}));

// ============================================================================
// Constants
// ============================================================================

const MODEL_BASE_PATH = '/models/face-api';

// Model loading status
let modelsLoaded = false;
let loadingPromise: Promise<void> | null = null;

// ============================================================================
// Model Loading
// ============================================================================

/**
 * Load all required face detection models
 * Uses lazy loading - models are only loaded once when first needed
 */
export async function loadFaceModels(basePath: string = MODEL_BASE_PATH): Promise<void> {
  console.log('[ZK Model Loader] loadFaceModels called');
  console.log('[ZK Model Loader] modelsLoaded:', modelsLoaded);
  console.log('[ZK Model Loader] loadingPromise:', !!loadingPromise);

  // Return immediately if already loaded
  if (modelsLoaded) {
    console.log('[ZK Model Loader] Models already loaded, returning');
    return;
  }

  // Return existing promise if loading in progress
  if (loadingPromise) {
    console.log('[ZK Model Loader] Loading already in progress, waiting...');
    return loadingPromise;
  }

  // Start loading
  console.log('[ZK Model Loader] Starting new model load...');
  loadingPromise = doLoadModels(basePath);

  try {
    await loadingPromise;
    modelsLoaded = true;
    console.log('[ZK Model Loader] modelsLoaded set to true');
  } finally {
    loadingPromise = null;
  }
}

/**
 * Actually load the models
 */
async function doLoadModels(basePath: string): Promise<void> {
  console.log('[ZK Model Loader] doLoadModels called with basePath:', basePath);

  // Check if faceapi is available
  console.log('[ZK Model Loader] Checking if faceapi is available...');
  console.log('[ZK Model Loader] typeof faceapi:', typeof faceapi);

  if (typeof faceapi === 'undefined') {
    console.error('[ZK Model Loader] faceapi is undefined! The face-api.js library is not loaded.');
    console.error('[ZK Model Loader] Make sure to: npm install face-api.js');
    console.error('[ZK Model Loader] And import it in your entry point or this file');
    throw new Error(
      'face-api.js is not loaded. Please ensure the library is installed and imported.'
    );
  }

  console.log('[ZK Model Loader] faceapi is available');
  console.log('[ZK Model Loader] faceapi.nets available:', Object.keys(faceapi.nets || {}));
  console.log('[ZK Model Loader] Loading face detection models from:', basePath);

  try {
    // Load models in parallel for faster loading
    console.log('[ZK Model Loader] Starting parallel model load...');

    await Promise.all([
      // TinyFaceDetector - fast and lightweight face detection
      (async () => {
        console.log('[ZK Model Loader] Loading TinyFaceDetector...');
        await faceapi.nets.tinyFaceDetector.loadFromUri(basePath);
        console.log('[ZK Model Loader] TinyFaceDetector loaded!');
      })(),

      // FaceLandmark68Net - 68 facial landmark points
      (async () => {
        console.log('[ZK Model Loader] Loading FaceLandmark68Net...');
        await faceapi.nets.faceLandmark68Net.loadFromUri(basePath);
        console.log('[ZK Model Loader] FaceLandmark68Net loaded!');
      })(),

      // FaceRecognitionNet - 128-dimensional face descriptor
      (async () => {
        console.log('[ZK Model Loader] Loading FaceRecognitionNet...');
        await faceapi.nets.faceRecognitionNet.loadFromUri(basePath);
        console.log('[ZK Model Loader] FaceRecognitionNet loaded!');
      })(),
    ]);

    console.log('[ZK Model Loader] All face detection models loaded successfully!');
  } catch (error) {
    console.error('[ZK Model Loader] Failed to load face detection models:', error);
    console.error('[ZK Model Loader] Make sure model files exist in:', basePath);
    console.error('[ZK Model Loader] Required files: tiny_face_detector_model-*, face_landmark_68_model-*, face_recognition_model-*');
    throw new Error(
      `Failed to load face detection models: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
}

/**
 * Check if models are loaded
 */
export function areModelsLoaded(): boolean {
  return modelsLoaded;
}

/**
 * Get model loading status
 */
export function getModelStatus(): {
  loaded: boolean;
  loading: boolean;
} {
  return {
    loaded: modelsLoaded,
    loading: loadingPromise !== null,
  };
}

// ============================================================================
// Model Download Helper
// ============================================================================

/**
 * Instructions for downloading models
 *
 * Models should be downloaded from the face-api.js repository:
 * https://github.com/justadudewhohacks/face-api.js/tree/master/weights
 *
 * Required models:
 * - tiny_face_detector_model-weights_manifest.json
 * - tiny_face_detector_model-shard1
 * - face_landmark_68_model-weights_manifest.json
 * - face_landmark_68_model-shard1
 * - face_recognition_model-weights_manifest.json
 * - face_recognition_model-shard1
 *
 * Place them in: public/models/face-api/
 */
export const MODEL_DOWNLOAD_INSTRUCTIONS = `
To set up face detection models:

1. Create the models directory:
   mkdir -p public/models/face-api

2. Download models from:
   https://github.com/justadudewhohacks/face-api.js/tree/master/weights

3. Required files:
   - tiny_face_detector_model-weights_manifest.json
   - tiny_face_detector_model-shard1
   - face_landmark_68_model-weights_manifest.json
   - face_landmark_68_model-shard1
   - face_recognition_model-weights_manifest.json
   - face_recognition_model-shard1

4. Or use this script:
   cd public/models/face-api
   curl -O https://raw.githubusercontent.com/justadudewhohacks/face-api.js/master/weights/tiny_face_detector_model-weights_manifest.json
   curl -O https://raw.githubusercontent.com/justadudewhohacks/face-api.js/master/weights/tiny_face_detector_model-shard1
   curl -O https://raw.githubusercontent.com/justadudewhohacks/face-api.js/master/weights/face_landmark_68_model-weights_manifest.json
   curl -O https://raw.githubusercontent.com/justadudewhohacks/face-api.js/master/weights/face_landmark_68_model-shard1
   curl -O https://raw.githubusercontent.com/justadudewhohacks/face-api.js/master/weights/face_recognition_model-weights_manifest.json
   curl -O https://raw.githubusercontent.com/justadudewhohacks/face-api.js/master/weights/face_recognition_model-shard1
`;
